/* eslint-disable class-methods-use-this */
// [uTheory](http://utheory.com) - Copyright (c) Gregory Ristow 2015
//
// ## Blank for Interactive Game
//
// Include description of object & useful info here, please!

import InteractivePrototype from './interactivePrototype.ts';
import utils from '../utils.ts';
import * as Skills from '../uTheory.Skills/skills.ts';
import * as Notation from '../notation.ts';
//
class ConjunctDisjunct extends InteractivePrototype {
  // Public constructor
  constructor(displayDiv, options = {}) {
    super();
    this.targetSpeedFromSkills = ConjunctDisjunct.registry.skills;
    this.settings = {
      instructionsPanel: {
        innerHTML:
          '<p>You will hear several notes. ' +
          'Are the notes stepwise (conjunct) ' +
          'or leapy (disjunct)?</p>',
        callback: this.playMusic,
      },
      instructions: true,
      editor: false,
      notation: false,
      piano: false,
      letters: {
        customButtons: [
          { text: '<u>P</u>lay again', value: 'play', hotKey: ['p', 'P'] },
          { tagName: 'br' },
          { text: '<u>S</u>tepwise', value: 'steps', hotKey: ['s', 'S'] },
          { text: '<u>L</u>eapy', value: 'leaps', hotKey: ['l', 'L'] },
        ],
      },
      checkMark: {},
      player: {},
      oneTryOnly: false,
      progressTracker: true,
    };

    if (options.drill && !options.maxQuestions) options.maxQuestions = 10;

    this.init(displayDiv, options);
  }

  init(displayDiv, options) {
    if (options.skills && utils.isString(options.skills)) {
      options.skills = [options.skills];
    }

    options.noPlay = true;
    super.init(displayDiv, options);
  }

  generateQuestions(questions) {
    this.questionPrompt = 'Are the notes you hear stepwise or leapy?';

    this.maxQuestions = this.options.maxQuestions || this.options.questions.length || 10;

    this.questions = this.options.drill
      ? this.generateDrillQuestions()
      : this.processManualQuestions(questions);
  }

  generateDrillQuestions() {
    const skills = this.options.skills;
    const maxQuestions = this.options.maxQuestions || 10;
    const qPerSkill = Math.ceil(maxQuestions / skills.length);
    console.log(`Making ${qPerSkill} questions per skill.`);
    return skills.reduce((questions, skill) => {
      console.log(`generating questions for: ${skill}`);
      skill = Skills.makeString(skill);
      console.log(`generating questions for: ${skill}`);
      for (let i = 0; i < qPerSkill; i++) {
        const nh = this.generateQuestion(skill);
        console.log(`question notes: ${nh}`);
        questions.push({
          noteHolder: nh,
          answer: this.isConjunct(nh) ? 'steps' : 'leaps',
          skills: skill,
        });
      }
      return questions;
    }, []);
  }

  processManualQuestions(questions) {
    return questions.map(this.questionFromString, this);
  }

  questionFromString(q) {
    const notes = q.split(',').map((n) => n.trim());
    const nh = new Notation.NoteHolder(notes);
    const skills = this.options.skills;
    if (!skills || skills.length !== 1) {
      throw new Error(
        'For manual conjunct/disjunct questions ' +
          'options.skills must be an array of one skill only.'
      );
    }

    return {
      noteHolder: nh,
      answer: this.isConjunct(nh) ? 'steps' : 'leaps',
      skills: this.options.skills[0],
    };
  }

  isConjunct(nh) {
    const hasLeap = nh.notes.reduce((a, b) => {
      if (a === true) return true;
      if (Math.abs(a.getGenericIntervalTo(b)) > 2) return true;
      return b;
    });
    return hasLeap !== true;
  }

  generateQuestion(skill) {
    let notes;
    skill = Skills.makeString(skill);
    if (!skill) {
      throw new Error('Skill must be provided to generate conjunct / disjunct question.');
    }
    switch (skill) {
      case 'conjunct_disjunct_basic':
        notes = Math.random() > 0.5 ? this.conjunct() : this.disjunct(1);
        notes.length = 4;
        break;
      case 'conjunct_disjunct_direction_changes':
        notes = Math.random() > 0.5 ? this.conjunct(true) : this.disjunct(2);
        notes.length = 4;
        break;
      case 'conjunct_disjunct_steps_vs_thirds':
        notes = Math.random() > 0.5 ? this.conjunct(true) : this.conjunct(true, 3);
        notes.length = 3;
        break;
      case 'conjunct_disjunct_2_note':
        notes = Math.random() > 0.5 ? this.conjunct(true) : this.conjunct(true, 3);
        notes.length = 2;
        break;
    }
    return notes;
  }

  conjunct(changeDirection, interval) {
    console.log('conjunct');
    interval = interval || 2;
    // Pick a random key
    const key = Notation.KeySignature.randomKey();
    // Pick a random scale degree
    const note = Notation.Note.randomNote({ lowest: 'C3', highest: 'B4' });

    const direction = function () {
      return changeDirection && Math.random() > 0.5 ? '-' : '';
    };

    note.accidental = key.getAccidentalAtNote(note);
    const notes = [note];
    for (let i = 1; i < 6; i++) {
      notes.push(notes[i - 1].getNoteAtGeneric(direction() + interval));
      notes[i].accidental = key.getAccidentalAtNote(notes[i]);
    }

    const nh = new Notation.NoteHolder(notes);
    if (Math.random() > 0.5) nh.notes.reverse();
    nh.notes.length = 4;
    return nh;
  }

  disjunct(level) {
    let notes;
    let note;
    let highestNote;
    let interval;
    let chord;
    switch (level) {
      // Random stacks of 4ths, 5ths or 6ths:
      case 1:
        notes = [];
        interval = [5, 7, 8, 9][Math.floor(Math.random() * 4)];
        highestNote = Notation.Note.fromMidi(91 - interval * 6);
        notes[0] = Notation.Note.randomNote({
          lowest: 'C2',
          highest: highestNote,
        });
        for (let i = 1; i < 6; i++) {
          notes.push(Notation.Note.fromMidi(notes[i - 1].getMidiNumber() + interval));
        }
        notes = new Notation.NoteHolder(notes);
        break;
      case 2:
        if (Math.random() > 0.8) {
          notes = new Notation.NoteHolder(this.stacks(true));
        } else {
          chord = Notation.Chord.randomChord();
          // Maybe we shuffle close spacing:
          if (!chord.isSeventh) {
            chord.setInversion(~~(Math.random() * 3));
            notes = chord.noteHolder.clone();
            const octave = notes.notes[0].clone();
            octave.octave++;
            notes.push(octave);
          } else {
            notes = chord.noteHolder.clone();
          }
          utils.shuffle(notes.notes);
        }
        // Get a random chord in open spacing (level 1)
        break;
      case 3:
        // Get a 3-note arpeggio:
        notes = this.conjunct(true, 3);
        break;
      case 4:
        // Get any 2-notes, @3rd, 4th:
        note = Notation.Note.randomNote({ lowest: 'A2', highest: 'C5' });
        interval = ['m3', 'M3', 'P4'][Math.floor(Math.random() * 3)];
        notes = new Notation.NoteHolder([
          note,
          note.getNoteAt(interval) || note.getNoteAtGeneric(interval),
        ]);
        break;
    }
    return notes;
  }

  stacks(changeDirection) {
    const direction = function () {
      return changeDirection && Math.random() > 0.5 ? -1 : 1;
    };
    let i;
    let note = Notation.Note.randomNote({ highest: 'G3', lowest: 'C2' });
    note.accidental = 'b';
    const fifths = [note];
    for (i = 1; i < 4; i++) {
      const priorNote = fifths[i - 1];
      const newNote = Notation.Note.fromMidi(priorNote.getMidiNumber() + direction() * 7);
      fifths.push(newNote);
    }

    note = Notation.Note.randomNote({ highest: 'B3', lowest: 'E2' });
    note.accidental = '#';
    const fourths = [note];
    for (i = 1; i < 4; i++) {
      const priorNote = fourths[i - 1];
      const newNote = Notation.Note.fromMidi(priorNote.getMidiNumber() + direction() * 5);
      fourths.push(newNote);
    }

    return utils.shuffle([fourths, fifths])[0];
  }

  addListeners() {
    // Code to set callbacks & communication to eventually check an answer.
    this.letterPanel.addCustomButtonCallback(this.checkAnswer, this);
  }

  newQuestion() {
    super.newQuestion();
    if (this.questionNumber !== 0) this.playMusic();
  }

  playMusic() {
    this.player.stop();
    let duration = 0.4;
    this.currentQuestion.noteHolder.forEach((note, index) => {
      this.player.play(note, {
        startTime: index * duration,
        stopTime: index * duration + 2.05 * duration,
      });
      duration /= 1.08;
    }, this);
  }

  checkAnswer(answer) {
    if (answer === 'play') return this.playMusic();
    if (this.options.testMode === true) return this.checkpointAnswer(answer);
    if (this.rightAnswerDelay) return undefined;

    if (answer === this.currentQuestion.answer) {
      this.player.stop();
      this.rightAnswer(300);
    } else {
      this.wrongAnswer();
      this.playMusic();
    }
    return undefined;
  }

  gameOver() {
    this.instructionsDiv.innerHTML = 'Nice work!';
    this.instructionsDiv.setAttribute('role', 'alert');
    this.displayDiv.removeChild(this.lettersDiv);
    super.gameOver();
  }

  // ## CheckPoint Mode Questions:
  newCheckpointQuestion(questionString) {
    return this.loadingPromise.then(() => {
      const nh = this.questionFromString(questionString);
      this.currentQuestion = this.questionFromNoteHolder(nh);
      this.playMusic();
      return Promise.resolve(this.currentQuestion);
    });
  }

  // For checkpoint tests, the answer & data from the current question is
  // logged via a callback.
  getAnswerAndData(answer) {
    const q = this.currentQuestion;
    const answerAndData = {
      question: `${q.noteHolder}`,
      questionType: 'ConjunctDisjunct',
      questionText: this.instructionsDiv.innerHTML,
      questionSVG: null,
      studentAnswerText: answer,
      studentAnswerSVG: null,
      correctAnswerText: q.answer,
      rightAnswers: answer === q.answer ? 1 : 0,
      wrongAnswers: answer === q.answer ? 0 : 1,
      skills: q.skills,
    };
    return answerAndData;
  }
}

ConjunctDisjunct.registry = {
  description: 'Identify Conjunct & Disjunct Motion',
  skills: [
    'conjunct_disjunct_basic',
    'conjunct_disjunct_direction_changes',
    'conjunct_disjunct_steps_vs_thirds',
    'conjunct_disjunct_2_note',
  ],
};

export default ConjunctDisjunct;
